// Strings of network requests (feature spec 17, phase 2): the Traffic tab, plan review, the deployment side, the
// Variables rows, the designer and the activity sentences (spec 11 6.15.21–6.15.27). `strings.networks.requests`.
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export const networkRequests = {
  // Traffic tab (6.15.21)
  tab: 'Traffic',
  introMicro: (flows: number, approved: number) =>
    `Deny by default between and inside attachments. Allowed: blueprint connections inside each deployment (${flows}) and approved requests (${approved}).`,
  introOpen: (topology: string) =>
    `Now: ${topology} · all traffic between attachments is allowed. Decisions are recorded now and enforced once the network uses micro-segmentation.`,
  introClosed: 'Attachments can’t reach each other; approved requests can’t be served.',
  requests: 'Requests',
  filters: 'Filters',
  needsDecision: (n: number) => `Needs a decision (${n})`,
  all: 'All',
  neededForMicro: (n: number) => `Needed for micro-segmentation (${n})`,
  decisionFilter: 'Decision',
  enforcementFilter: 'Enforcement',
  sourceFilter: 'Source blueprint',
  targetFilter: 'Target blueprint',
  any: 'Any',
  pairFilter: (a: string, b: string) => `Between ${a} and ${b}`,
  clearPair: 'Show all pairs',
  approveSelected: (n: number) => `Approve selected (${n})`,
  rejectSelected: 'Reject selected…',
  approveAllPending: (n: number) => `Approve all pending (${n})`,
  columns: {
    select: 'Select',
    sourceTarget: 'Source → target',
    decision: 'Decision',
    enforcement: 'Enforcement',
    details: 'Details',
    actions: 'Actions',
  },
  tableLabel: (network: string) => `Requests of ${network}`,
  groupName: (source: string, requestId: string, target: string, resource: string, port: string) =>
    `${source} · ${requestId} → ${target} / ${resource}${port ? ` · ${port}` : ''}`,
  instanceName: (source: string, target: string, region: string) => `${source} → ${target} · ${region}`,
  selectInstance: (name: string) => `Select ${name}`,
  noTarget: 'no target yet',
  tcp: (port: number) => `TCP ${port}`,
  proposedBy: (blueprint: string, version: number, target: string, port: number) =>
    `Proposed by a plan of ${blueprint} v${version}, not applied: ${target} on ${port}`,
  decision: {
    pending: 'Pending',
    approved: 'Approved',
    rejected: 'Rejected',
    revoked: 'Revoked',
  } as Record<string, string>,
  shapeBy: (blueprint: string, version: number, by: string | null) =>
    `${blueprint} v${version}${by ? ` by ${by}` : ''}`,
  decidedBy: (by: string, when: string, reason: string | null) =>
    `by ${by} · ${when}${reason ? ` · “${reason}”` : ''}`,
  askedAgainLine: (when: string, by: string) => `Asked again on ${when} by ${by}`,
  askedAgainReason: (reason: string) => `“${reason}”`,
  showDetails: (name: string) => `Details of ${name}`,
  noRequests: 'No network requests yet. Blueprints declare them; their deployments’ plans propose them here.',
  noMatch: 'No request matches the filters.',
  selectFirst: 'Select requests first.',
  staleCount: (n: number) => `${n} stale`,
  approve: 'Approve',
  approveEllipsis: 'Approve…',
  reject: 'Reject…',
  revoke: 'Revoke…',
  restore: 'Restore…',
  askAgain: 'Ask again…',
  retry: 'Retry',
  open: 'Open',
  askAdminRetry: (network: string) => `Ask a network admin of ${network} to retry`,
  // Effects before acting (U2.1)
  approveTitle: (n: number) => (n === 1 ? 'Approve this request?' : `Approve ${n} requests?`),
  approveConfirm: (n: number) => (n === 1 ? 'Approve' : `Approve ${n}`),
  recordedToast: (network: string) => `Recorded; enforced once ${network} uses micro-segmentation`,
  approvedToast: (n: number) => (n === 1 ? 'Approved.' : `${n} requests approved.`),
  decidedToast: (decision: string, n: number) =>
    n === 1 ? `Request ${decision}.` : `${n} requests ${decision}.`,
  queuedRun: 'A decision run is queued.',
  revokeTitle: (name: string) => `Revoke ${name}?`,
  rejectTitle: (n: number) => (n === 1 ? 'Reject this request?' : `Reject ${n} requests?`),
  restoreTitle: (name: string) => `Restore ${name}?`,
  askAgainTitle: (requestId: string) => `Ask again about ${requestId}`,
  askAgainHelp: (network: string) =>
    `A network admin of ${network} decides again. You can ask once per shape of the request.`,
  reason: 'Reason',
  reasonRequired: 'Say why; the reason is recorded with the decision.',
  reasonMissing: 'Enter a reason.',
  confirmRevoke: 'Revoke',
  confirmReject: 'Reject',
  confirmRestore: 'Restore',
  confirmAskAgain: 'Ask again',
  sameApprover: 'You saved the version that asked for this; another network admin decides it',
  groupLimit: (network: string, region: string, limit: number) =>
    `${network}’s firewall in ${region} holds ${limit} rule groups; revoke a request`,
  groupCount: (used: number, limit: number, region: string) => `${used} of ${limit} rule groups in ${region}`,
  roomLeft: (approvals: number, region: string, room: number) =>
    `${plural(approvals, 'approval')} need ${plural(approvals, 'group')}; ${region} has room for ${room}`,
  summaryTitle: (decision: string, n: number) => `${decision} ${plural(n, 'request')}`,
  summaryEffect: 'Effect',
  changedMeanwhile: (name: string, by: string | null) =>
    `${name} was decided${by ? ` by ${by}` : ''} meanwhile`,
  decideOthers: (n: number) => `Decide the other ${n}`,
  reloaded: 'The changed requests were reloaded; check them again.',
  requestChanged: 'This request changed; check it again',
  // Generated flows and egress (the Segmentation part of the tab)
  generatedFlows: 'Generated flows',
  generatedFlowsNote: (n: number) => `from each blueprint’s own connections · ${n} · read-only`,
  flowSource: (deployment: string, region: string) => `all apps of ${deployment} in ${region}`,
  flowWhy: (source: string, target: string) => `blueprint connection ${source} → ${target}`,
  checkedByFirewall: 'checked by the hub firewall',
  noFlows: 'No generated flows: no attached deployment connects its apps to private resources yet.',
  egress: 'Egress',
  egressNote: 'Service-tag rules reach the whole service, not only this deployment’s namespace.',
  egressAllowed: (hosts: string) => `Allowed host names: ${hosts}`,
  egressNone: 'No allowed host names.',
  egressDirect: 'Internet traffic goes out directly.',
  egressThroughFirewall: 'Internet traffic goes through the hub firewall.',
  egressNotFiltered:
    'Deny by default applies between and inside attachments, not to the internet. Turn on the firewall with egress through it to filter internet traffic.',
  awsEgressNotFiltered:
    'Outbound internet traffic from attached deployments on AWS isn’t filtered. Turn on AWS Network Firewall with egress through it to filter it.',
  turnOn: 'Turn on',
  awsCentralNat: (ips: string) => `AWS: through the central NAT gateways ${ips}`,
  awsCentralNatPending:
    'AWS: through the central NAT gateways (their addresses come with the network’s apply)',
  // Plan review (6.15.23)
  planGroup: 'Network requests',
  planChange: {
    new: 'New',
    changed: 'Changed',
    removed: 'Removed',
    unchanged: 'Unchanged',
  } as Record<string, string>,
  planRow: (requestId: string, source: string, target: string) => `${requestId} ${source} → ${target}`,
  planSettings: (alias: string) => `settings ${alias}_HOST, ${alias}_PORT`,
  proposedNeedsDecision: 'Proposed · needs a decision',
  notEnforcedShort: 'not enforced',
  closesChanged: (requestId: string, source: string, target: string, newTarget: string, network: string) =>
    `${requestId}: ${source}’s approved access to ${target} closes after this apply; the new target ${newTarget} isn’t approved in ${network} yet`,
  closesRemoved: (requestId: string, source: string, target: string) =>
    `${requestId} removed: ${source} loses access to ${target} after this apply`,
  blockedUntil: (network: string) => `Blocked until a network admin of ${network} approves it`,
  pendingCallout: (n: number, network: string) =>
    `${n === 1 ? '1 network request waits' : `${n} network requests wait`} for a network admin of ${network}.`,
  newRequestsCallout: (n: number) =>
    n === 1 ? '1 new network request will be proposed.' : `${n} new network requests will be proposed.`,
  viewRequests: 'View requests',
  // Deployment side (6.15.22)
  networkRequestsOf: 'Network requests of this blueprint',
  targetLabel: (requestId: string) => `Target of ${requestId}`,
  chooseTarget: 'Choose the target',
  suggested: 'suggested',
  suggestedHelp: 'Suggested; it is saved with Save settings.',
  inbound: 'Inbound requests',
  cardRequest: (requestId: string, target: string, state: string) => `${requestId} → ${target} · ${state}`,
  cardInbound: (source: string, resource: string) => `Inbound: ${source} → ${resource}`,
  inactive: 'Requests that stop',
  // Variables window (6.15.24)
  variableLocation: 'Network request',
  variableLabel: (requestId: string, target: string | null, state: string | null) =>
    [`Network request · ${requestId}`, ...(target ? [target] : []), ...(state ? [state] : [])].join(' · '),
  perDeployment: 'Resolved per deployment',
  notAttached: (deployment: string) => `Not set: ${deployment} isn’t attached to a network`,
  unresolved: (blueprint: string, network: string) =>
    `Not set: no ${blueprint} deployment is attached to ${network}`,
  ambiguous: 'Not set: choose the target',
  notSetPrefix: 'Not set: ',
  blocked: (network: string) => `Blocked until a network admin of ${network} approves it`,
  openSettings: 'Open Settings → Network',
  // Designer (6.15.25)
  requestAccess: 'Request access to another system…',
  requestAccessShort: 'Request access…',
  dialogTitle: (source: string) => `Request access from ${source}`,
  editTitle: (requestId: string) => `Edit ${requestId}`,
  blueprint: 'Blueprint',
  chooseBlueprint: 'Choose a blueprint',
  resource: 'Resource',
  chooseResource: 'Choose a resource',
  notReachable: 'Network requests can reach databases, web apps and function apps',
  behindPublicLoadBalancer: 'Behind an internet-facing load balancer',
  port: 'Port',
  portFixed: (port: number) => `TCP ${port}, from the resource type`,
  portUnknown: 'From the resource type',
  requestId: 'Request ID',
  requestIdHelp: 'Lowercase letters, digits and hyphens; unique in this blueprint.',
  alias: 'Alias',
  aliasHelp: 'Prefix of the settings the app gets.',
  reasonHelp: 'Network admins read it when they decide.',
  gives: (source: string, alias: string) => `Gives ${source} ${alias}_HOST and ${alias}_PORT.`,
  noCredentials: (target: string) =>
    `${target}’s database password isn’t shared: add a secret variable for the app’s credentials.`,
  addSecretVariable: 'Add a secret variable',
  saveRequest: 'Add request',
  updateRequest: 'Save changes',
  idTaken: 'Another request of this blueprint has this ID.',
  idInvalid: 'Use lowercase letters, digits and hyphens, starting with a letter.',
  required: 'Required.',
  loadingTarget: 'Loading the blueprint…',
  // Canvas (U2.5)
  edgeName: (requestId: string, source: string, target: string, state: string) =>
    `Network request ${requestId} from ${source} to ${target}, ${state}`,
  ghostName: (blueprint: string, resource: string, type: string, requestId: string, state: string) =>
    `${blueprint} · ${resource}, ${type} in another blueprint, target of request ${requestId}, ${state}`,
  ghostSubtitle: (type: string) => `another blueprint · ${type}`,
  ghostTarget: (requestId: string) => `target of ${requestId}`,
  targetUnknown: 'NETWORK_REQUEST_TARGET_UNKNOWN',
  targetUnknownText: 'The target blueprint or resource doesn’t exist.',
  stateIn: (state: string, network: string) => `${state} in ${network}`,
  summaryState: (parts: string) => parts,
  noDeploymentState: 'not deployed yet',
  targetNotChosen: (network: string) => `target not chosen in ${network}`,
  notAttachedIn: (network: string) => `Not attached in ${network}`,
  legendRequest: 'request',
  // Inspector (6.15.25)
  inspectorCard: 'Network requests',
  requestedBy: 'Requested by',
  openBlueprint: (name: string) => `Open ${name}`,
  edit: 'Edit…',
  remove: 'Remove…',
  removeTitle: (requestId: string) => `Remove ${requestId}?`,
  removeEffect: (deployments: string, target: string) =>
    `${deployments} lose access to ${target} after their next apply.`,
  removeEffectNone: 'No deployment has access through it yet; nothing closes.',
  editEffect: (networks: string, target: string, firstDeployment: string) =>
    `Approved in ${networks}: the new target needs a new approval in ${networks.includes(' and ') ? 'both' : 'it'}. Unless a network admin approves it before ${firstDeployment}’s next apply, the current access to ${target} closes with that apply.`,
  editEffectNone: 'Not approved anywhere yet: the changed request is decided like a new one.',
  credentialsNote: 'Requests give an address and a port, never the target’s credentials.',
  ghostInspector: (blueprint: string) => `A resource of ${blueprint}, the target of a network request.`,
  // Activity (6.15.26)
  activity: {
    created: (requestId: string) => `requested ${requestId}`,
    approved: 'approved',
    rejected: 'rejected',
    revoked: 'revoked',
    restored: 'restored',
    askedAgain: (requestId: string, reason: string) =>
      `asked again on ${requestId}${reason ? `: ${reason}` : ''}`,
    stale: (requestId: string) => `${requestId} became stale`,
    withdrawn: (requestId: string) => `${requestId} was withdrawn`,
    enforced: (run: string, requestId: string) => `Decision run ${run} opened ${requestId}`,
    enforcementFailed: (run: string, requestId: string) => `Decision run ${run} failed to open ${requestId}`,
  },
  decisionRun: (n: number) => `Decision run · ${plural(n, 'request')}`,
  activityKinds: {
    'network-request-created': 'Request created',
    'network-request-approved': 'Request approved',
    'network-request-rejected': 'Request rejected',
    'network-request-revoked': 'Request revoked',
    'network-request-restored': 'Request restored',
    'network-request-asked-again': 'Request asked again',
    'network-request-stale': 'Request stale',
    'network-request-withdrawn': 'Request withdrawn',
    'network-request-enforced': 'Request enforced',
    'network-request-enforcement-failed': 'Request enforcement failed',
  } as Record<string, string>,
  activityParts: {
    verbs: {
      created: 'requested',
      approved: 'approved',
      rejected: 'rejected',
      revoked: 'revoked',
      restored: 'restored',
    },
    detail: (detail: string) => detail.replace(/-/g, ' '),
    askedAgain: 'asked again on',
    decisionRun: (run: string) => `Decision run ${run}`,
    opened: 'opened',
    failedToOpen: 'failed to open',
    stale: 'became stale',
    withdrawn: 'was withdrawn',
  },
  // Codes (6.15.27)
  codes: {
    MESH_TOO_LARGE: 'Switch to hub-spoke',
    HUB_TRANSIT_REQUIRES_FIREWALL: 'Turn on the firewall or choose blocked',
    TOPOLOGY_CHOICE_REQUIRED: 'Choose how spokes reach each other',
    REQUEST_DECISION_INVALID: 'This request changed; check it again',
  } as Record<string, string>,
  decisionMismatch: (network: string) =>
    `Needs a reviewed network apply: ask a network admin to plan ${network}`,
  tooManyRules: (network: string, attachment: string) =>
    `${network} has too many rules in ${attachment}; remove requests or flows`,
};
